import React, { useEffect, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import useClickOutsideModal from "../common/useClickOutsideModal";

const NameCorrectionModal = ({
  isOpen,
  request,
  onClose,
  onApprove,
  onReject,
  isApproving = false,
  isRejecting = false,
}) => {
  const isSubmitting = isApproving || isRejecting;
  const [requestedName, setRequestedName] = useState("");

  useEffect(() => {
    if (isOpen && request) {
      setRequestedName(request.requestedName || "");
    }
  }, [isOpen, request]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const menuRef = useClickOutsideModal(onClose);

  if (!isOpen || !request) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4 sm:px-4 sm:py-6">
      <div ref={menuRef} className="flex max-h-[min(94vh,calc(100dvh-2rem))] w-full max-w-[1024px] flex-col overflow-hidden rounded-xl bg-white">
        <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] p-4 sm:p-6">
          <h3 className="text-lg font-bold text-[#111827] sm:text-2xl">
            Certificate Name Correction Request
          </h3>
          <button
            type="button"
            aria-label="Close name correction"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[#6B7280]"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-6">
          <div className="rounded-lg border border-[#E5E7EB] p-4 sm:p-8 lg:p-12">
            <div className="mb-6 flex items-center gap-2">
              <svg
                width="19"
                height="28"
                viewBox="0 0 19 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.19 16.985L12.795 9.37998L11.73 8.31499L4.125 15.935V16.985H5.19ZM5.805 18.485H2.625V15.305L11.205 6.72499C11.345 6.58498 11.52 6.51499 11.73 6.51499C11.94 6.51499 12.115 6.58498 12.255 6.72499L14.385 8.85499C14.535 8.99498 14.61 9.16998 14.61 9.37998C14.61 9.58998 14.535 9.76999 14.385 9.91998L5.805 18.485ZM2.625 19.985H16.125V21.485H2.625V19.985Z"
                  fill="#9333EA"
                />
              </svg>

              <h4 className="text-lg font-bold text-[#111827]">
                Name Correction Details
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-[#374151]">
                  Current Name (from registration)
                </label>
                <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3 text-base font-medium text-[#7F1D1D]">
                  {request.currentName || "N/A"}
                </div>
              </div>

              <div>
                <label
                  htmlFor="requested-name"
                  className="mb-2 block text-sm font-medium text-[#374151]"
                >
                  Requested Name (editable)
                </label>
                <input
                  id="requested-name"
                  type="text"
                  value={requestedName}
                  onChange={(event) => setRequestedName(event.target.value)}
                  className="w-full rounded-lg border border-[#86EFAC] bg-[#F0FDF4] p-3 text-base text-[#111827] outline-none focus:ring-1 focus:ring-[#86EFAC]"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="mb-2 block text-sm font-medium text-[#374151]">
                Reason for Correction
              </label>
              <div className="min-h-[50px] rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-base text-[#1E3A8A]">
                {request.reason || "N/A"}
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <X size={16} strokeWidth={2} />
              Cancel
            </button>

            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onReject?.(request)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#FCA5A5] bg-white px-4 py-2 text-sm font-medium text-[#B91C1C] transition hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <X size={16} strokeWidth={2} />
              {isRejecting ? "Rejecting..." : "Reject Request"}
            </button>

            <button
              type="button"
              disabled={isSubmitting}
              onClick={() =>
                onApprove?.({
                  ...request,
                  requestedName: requestedName.trim() || request.requestedName,
                })
              }
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#9333EA] to-[#F97316] px-4 py-2 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Check size={16} strokeWidth={2.5} />
              {isApproving ? "Updating..." : "Approve & Update Name"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameCorrectionModal;
